// Excel column indices (0-based): E=4, F=5. E holds Strip/Cut, F holds Reuse/Fill.
const VALUE_COL_A = 4;
const VALUE_COL_B = 5;

// How many leading columns to scan for a row's label — the description
// column isn't fixed across client workbooks, so (like the POS/Compound
// importers) we scan a range rather than assume one exact column index.
const LABEL_SCAN_COLS = 8;

const normalizeLabel = (v: unknown): string =>
  String(v ?? '').trim().toUpperCase().replace(/\s+/g, ' ');

const SECTION_TRIGGERS: Array<{ pattern: RegExp; key: 'topsoil' | 'subsoil' }> = [
  { pattern: /^TOP\s*SOIL$/, key: 'topsoil' },
  { pattern: /^SUB\s*SOIL$/, key: 'subsoil' },
];

// Manual-input fields only — rows computed from Roads/Foul/Storm state
// (Road / Footpaths, Ponds, Road, Paths, Foul Sewers, Storm Sewers) have no
// entry here, so a matching sheet row is simply skipped rather than
// overwritten. Ponds Strip/Re-use are computed from Storm Sewers' Ponds &
// Swales build-up (see SpoilReconciliation.tsx), not imported here.
const TOPSOIL_FIELD_MAP: Record<string, [string, string]> = {
  'POS':                ['topsoilPOSStrip',                'topsoilPOSReuse'],
  'ATTENUATION TANKS':  ['topsoilAttenuationTanksStrip',  'topsoilAttenuationTanksReuse'],
  'CULVERTS':           ['topsoilCulvertsStrip',          'topsoilCulvertsReuse'],
};

// Road's Cut is computed from Roads state (not imported) — only its Fill is
// a manual input, so the Cut half of the pair is `null` and left untouched.
// Ponds Cut/Fill are computed from Storm Sewers' Ponds & Swales build-up
// (see SpoilReconciliation.tsx), so "Ponds" has no entry here either.
const SUBSOIL_FIELD_MAP: Record<string, [string | null, string | null]> = {
  'ROAD':               [null,                            'subsoilRoadFill'],
  'POS':                ['subsoilPOSCut',                 'subsoilPOSFill'],
  'ATTENUATION TANKS':  ['subsoilAttenuationTanksCut',    'subsoilAttenuationTanksFill'],
  'CULVERTS':           ['subsoilCulvertsCut',            'subsoilCulvertsFill'],
  'MAINS SERVICES':     ['subsoilMainsServicesCut',       'subsoilMainsServicesFill'],
};

const scanForSectionTrigger = (row: unknown[]): 'topsoil' | 'subsoil' | null => {
  const scan = Math.min(row.length, LABEL_SCAN_COLS);
  for (let c = 0; c < scan; c++) {
    const v = normalizeLabel(row[c]);
    if (!v) continue;
    const trigger = SECTION_TRIGGERS.find((t) => t.pattern.test(v));
    if (trigger) return trigger.key;
  }
  return null;
};

const scanForFieldPair = (
  row: unknown[],
  map: Record<string, [string | null, string | null]>,
): [string | null, string | null] | null => {
  const scan = Math.min(row.length, LABEL_SCAN_COLS);
  for (let c = 0; c < scan; c++) {
    const v = normalizeLabel(row[c]);
    if (v && map[v]) return map[v];
  }
  return null;
};

const toNum = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const s = String(v).trim();
  if (s.startsWith('#')) return null; // Excel formula error
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
};

const parseSpoilReconciliationSheet = (rows: unknown[][]): Record<string, number> => {
  const result: Record<string, number> = {};
  let currentMap: Record<string, [string | null, string | null]> | null = null;

  for (const row of rows) {
    const sectionKey = scanForSectionTrigger(row);
    if (sectionKey) {
      currentMap = sectionKey === 'topsoil' ? TOPSOIL_FIELD_MAP : SUBSOIL_FIELD_MAP;
      continue;
    }

    if (!currentMap) continue;

    const fieldPair = scanForFieldPair(row, currentMap);
    if (!fieldPair) continue; // calculated / unrecognized row — skip

    const [fieldStripOrCut, fieldReuseOrFill] = fieldPair;
    const a = toNum(row[VALUE_COL_A]);
    const b = toNum(row[VALUE_COL_B]);
    if (fieldStripOrCut && a !== null) result[fieldStripOrCut] = a;
    if (fieldReuseOrFill && b !== null) result[fieldReuseOrFill] = b;
  }

  return result;
};

export const parseSpoilReconciliationFromExcel = async (
  file: File,
): Promise<Record<string, number>> => {
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', raw: true });

  // Accept the client sheet's actual spelling ("Reconcilliation", double L)
  // as well as the correctly-spelled variant.
  const sheetName =
    workbook.SheetNames.find((n) => /^spoil\s*reconcil+iation$/i.test(n.trim()));

  if (!sheetName) {
    throw new Error(
      `Sheet "Spoil Reconcilliation" not found. Available sheets: ${workbook.SheetNames.join(', ')}`,
    );
  }

  const ws = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json<unknown[]>(ws, {
    header: 1,
    raw: true,
    defval: '',
  }) as unknown[][];

  return parseSpoilReconciliationSheet(rows);
};
